import { FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { COUNTRIES, DEFAULT_COUNTRY } from "@/lib/countries";
import { US_STATES } from "@/lib/usStates";
import type { AddressDto } from "@/types/counterparties";

interface AddressFieldsProps {
  value: AddressDto;
  onChange: (next: AddressDto) => void;
  required?: boolean;
  /** Override field labels (e.g. "HQ city" for Business). */
  labels?: Partial<Record<keyof AddressDto, string>>;
  idPrefix?: string;
  disabled?: boolean;
}

const DEFAULT_LABELS: Record<keyof AddressDto, string> = {
  streetAddress1: "Street address",
  streetAddress2: "Street address (line 2)",
  city: "City",
  state: "State",
  zip: "ZIP",
  country: "Country",
};

// When country is not US, we relabel the State input — most countries call
// the equivalent administrative subdivision something else and a 2-letter
// USPS code obviously doesn't apply.
const NON_US_STATE_LABEL = "State / Province / Region";
const NON_US_ZIP_LABEL = "Postal code";

/**
 * Shared address-collection block. Country and US state are dropdowns bound
 * to the canonical catalogs in `@/lib/countries` and `@/lib/usStates`; the
 * matching backend validators (PhoenixCapital.Core.Lookups) reject any value
 * that isn't in those catalogs.
 *
 * Behaviour notes:
 * - Country defaults to "US" via the consumer's initial state (see
 *   {@link EMPTY_ADDRESS} in `@/types/counterparties`); this component does
 *   not mutate `value` on mount.
 * - When country ≠ "US" the State field switches to a free-text input with
 *   a "State / Province / Region" label. We do NOT clear the prior US-state
 *   code automatically — losing the value on a misclick is worse than
 *   showing a now-meaningless 2-letter code that the user can edit.
 */
export function AddressFields({
  value,
  onChange,
  required,
  labels,
  idPrefix,
  disabled,
}: AddressFieldsProps) {
  const set = <K extends keyof AddressDto>(k: K, v: AddressDto[K]) =>
    onChange({ ...value, [k]: v });
  const L = { ...DEFAULT_LABELS, ...(labels ?? {}) };
  const id = (k: string) => (idPrefix ? `${idPrefix}-${k}` : undefined);
  const country = value.country || DEFAULT_COUNTRY;
  const isUs = country === "US";

  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-space-3">
      <FormField label={L.streetAddress1} required={required} className="md:col-span-3" htmlFor={id("addr1")}>
        <Input
          id={id("addr1")}
          value={value.streetAddress1}
          onChange={(e) => set("streetAddress1", e.target.value)}
          disabled={disabled}
        />
      </FormField>
      <FormField label={L.streetAddress2} className="md:col-span-3" htmlFor={id("addr2")}>
        <Input
          id={id("addr2")}
          value={value.streetAddress2 ?? ""}
          onChange={(e) => set("streetAddress2", e.target.value || null)}
          disabled={disabled}
        />
      </FormField>

      <FormField label={L.city} required={required} className="md:col-span-2" htmlFor={id("city")}>
        <Input
          id={id("city")}
          value={value.city}
          onChange={(e) => set("city", e.target.value)}
          disabled={disabled}
        />
      </FormField>

      <FormField
        label={isUs ? L.state : NON_US_STATE_LABEL}
        required={required && isUs}
        className="md:col-span-2"
        htmlFor={id("state")}
      >
        {isUs ? (
          <Select
            id={id("state")}
            value={value.state}
            onChange={(e) => set("state", e.target.value)}
            disabled={disabled}
          >
            <option value="">— Select state —</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            id={id("state")}
            value={value.state}
            onChange={(e) => set("state", e.target.value)}
            disabled={disabled}
          />
        )}
      </FormField>

      <FormField
        label={isUs ? L.zip : NON_US_ZIP_LABEL}
        required={required}
        className="md:col-span-2"
        htmlFor={id("zip")}
      >
        <Input
          id={id("zip")}
          maxLength={10}
          value={value.zip}
          onChange={(e) => set("zip", e.target.value)}
          placeholder={isUs ? "12345 or 12345-6789" : undefined}
          disabled={disabled}
        />
      </FormField>

      <FormField label={L.country} required={required} className="md:col-span-3" htmlFor={id("country")}>
        <Select
          id={id("country")}
          value={country}
          onChange={(e) => set("country", e.target.value)}
          disabled={disabled}
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </Select>
      </FormField>
    </div>
  );
}
